'use client';

import { Icons } from '@shared/components/content/icons';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Separator } from '@shared/components/ui/separator';
import { Skeleton } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';
import * as React from 'react';

export type CascadingFilterNode = {
  value: string;
  label: string;
  children?: readonly CascadingFilterNode[];
};

type Props = {
  /** Pill label, as on every other filter — e.g. "Category". */
  title: string;
  nodes: readonly CascadingFilterNode[];
  /** The applied selection, outermost first. Empty means unfiltered. */
  selectedPath: readonly string[];
  /** The new path, outermost first. Empty clears the filter. */
  onSelect: (path: string[]) => void;
  onClear: () => void;
  /** Heading over the first column, e.g. "Categories". */
  rootHeading: string;
  /** Leading row of the first column, which clears the filter. */
  clearLabel: string;
  /** The tree is still being fetched. */
  isLoading?: boolean;
  emptyMessage?: string;
};

/** The nodes to show at each level along a path, and what opened each one. */
function columnsFor(
  nodes: readonly CascadingFilterNode[],
  path: readonly string[]
) {
  const columns = [{ nodes, parentLabel: null as string | null }];
  let level = nodes;
  for (const value of path) {
    const node = level.find((candidate) => candidate.value === value);
    if (!node?.children?.length) break;
    columns.push({ nodes: node.children, parentLabel: node.label });
    level = node.children;
  }
  return columns;
}

/** The label at each step of a path, stopping where the path leaves the tree. */
export function labelsFor(
  nodes: readonly CascadingFilterNode[],
  path: readonly string[]
) {
  const labels: string[] = [];
  let level: readonly CascadingFilterNode[] | undefined = nodes;
  for (const value of path) {
    const node = level?.find((candidate) => candidate.value === value);
    if (!node) break;
    labels.push(node.label);
    level = node.children;
  }
  return labels;
}

/**
 * A filter over a hierarchy, shown as side-by-side columns. Moving across a
 * branch opens the level beneath it without changing the filter, so the tree
 * can be explored freely; only a click applies, and the pill reads the applied
 * path back as a breadcrumb.
 *
 * The whole tree is supplied up front, so browsing costs nothing and a column
 * never arrives late or under a parent that has since changed.
 */
export function CascadingFilter({
  title,
  nodes,
  selectedPath,
  onSelect,
  onClear,
  rootHeading,
  clearLabel,
  isLoading,
  emptyMessage,
}: Props) {
  const [open, setOpen] = React.useState(false);
  // What the columns are showing. Follows the cursor, not the filter.
  const [previewPath, setPreviewPath] = React.useState<readonly string[]>([]);
  const [active, setActive] = React.useState<[number, number] | null>(null);
  const rowRefs = React.useRef(new Map<string, HTMLButtonElement>());

  // Labels where the tree knows the node, the raw value where it doesn't: a
  // scoped tree can drop the applied branch, and the pill must still read.
  const breadcrumb = React.useMemo(() => {
    const labels = labelsFor(nodes, selectedPath);
    return [...labels, ...selectedPath.slice(labels.length)];
  }, [nodes, selectedPath]);
  const hasSelection = selectedPath.length > 0;
  const columns = React.useMemo(
    () => columnsFor(nodes, previewPath),
    [nodes, previewPath]
  );

  // Opening starts where the filter stands, so the applied branch is in view.
  // Only on open: re-syncing mid-browse would snap the columns back.
  React.useEffect(() => {
    if (open) setPreviewPath(selectedPath);
    else setActive(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open || !active) return;
    rowRefs.current.get(`${active[0]}:${active[1]}`)?.focus();
  }, [open, active, columns]);

  const registerRow = (key: string) => (node: HTMLButtonElement | null) => {
    if (node) rowRefs.current.set(key, node);
    else rowRefs.current.delete(key);
  };

  const onReset = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    onClear();
  };

  /** Browsing only moves the columns; the filter is untouched until a click. */
  const preview = (level: number, value: string) =>
    setPreviewPath([...previewPath.slice(0, level), value]);

  // A click applies and closes at any tier; browsing deeper is by hover, so
  // the columns beneath a branch are reached without committing to it.
  const select = (level: number, node: CascadingFilterNode | null) => {
    onSelect(node ? [...previewPath.slice(0, level), node.value] : []);
    setOpen(false);
  };

  const rowCount = (level: number) =>
    columns[level].nodes.length + (level === 0 ? 1 : 0);

  const onKeyDown = (
    event: React.KeyboardEvent,
    level: number,
    row: number
  ) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const total = rowCount(level);
      const next = row + (event.key === 'ArrowDown' ? 1 : -1);
      setActive([level, (next + total) % total]);
    } else if (event.key === 'ArrowLeft' && level > 0) {
      event.preventDefault();
      setActive([level - 1, 0]);
    } else if (event.key === 'ArrowRight' && columns[level + 1]) {
      event.preventDefault();
      setActive([level + 1, 0]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'border-dashed',
            hasSelection &&
              'border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm hover:bg-primary-600 hover:text-gray-00'
          )}
        >
          {hasSelection ? (
            <div
              role="button"
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onReset}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Icons.xCircle className="mr-2 size-4" />
            </div>
          ) : (
            <Icons.plusCircle className="mr-2 size-4" />
          )}
          {title}
          {hasSelection && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />
              {/* The deepest node implies its parents, so the pill shows it
                  alone and keeps the full path for the tooltip and the
                  columns inside. */}
              <SimpleTooltip
                trigger={
                  <Badge
                    variant="secondary"
                    className="rounded-sm border-transparent bg-primary-500 px-1 font-normal text-gray-00"
                  >
                    {breadcrumb[breadcrumb.length - 1]}
                  </Badge>
                }
                content={breadcrumb.join(' › ')}
              />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex items-stretch">
          {columns.map((column, level) => (
            <div
              key={column.parentLabel ?? rootHeading}
              className={cn(
                'flex w-[16.5rem] flex-col',
                level > 0 && 'border-l border-border'
              )}
            >
              <p className="truncate pb-1 pl-9 pr-3 pt-3 text-sm font-light text-muted-foreground">
                {column.parentLabel ?? rootHeading}
              </p>
              <div className="max-h-[18.75rem] flex-1 overflow-y-auto overflow-x-hidden p-1">
                {level === 0 && isLoading ? (
                  <CascadingFilterLoadingRows />
                ) : (
                  <>
                    {level === 0 && (
                      <CascadingFilterRow
                        ref={registerRow('0:0')}
                        label={clearLabel}
                        selected={!hasSelection}
                        onSelect={() => select(0, null)}
                        onKeyDown={(event) => onKeyDown(event, 0, 0)}
                      />
                    )}
                    {column.nodes.map((node, index) => {
                      const row = index + (level === 0 ? 1 : 0);
                      return (
                        <CascadingFilterRow
                          key={node.value}
                          ref={registerRow(`${level}:${row}`)}
                          label={node.label}
                          hasChildren={!!node.children?.length}
                          selected={selectedPath[level] === node.value}
                          browsing={previewPath[level] === node.value}
                          onSelect={() => select(level, node)}
                          onBrowse={() => preview(level, node.value)}
                          onKeyDown={(event) => onKeyDown(event, level, row)}
                        />
                      );
                    })}
                    {level === 0 && column.nodes.length === 0 && (
                      <p className="py-6 text-center text-sm">
                        {emptyMessage ?? 'No results found.'}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Placeholder rows on the same rhythm as the real ones: each sits in a box the
 * height of a row, so the list doesn't jump when the tree arrives.
 */
const LOADING_ROW_WIDTHS = ['w-full', 'w-4/5', 'w-11/12', 'w-3/4', 'w-5/6'];

function CascadingFilterLoadingRows() {
  return (
    <div aria-busy>
      {LOADING_ROW_WIDTHS.map((width, row) => (
        <div key={row} className="flex h-8 items-center px-2">
          <Skeleton className={cn('h-3.5 rounded-sm', width)} />
        </div>
      ))}
    </div>
  );
}

/** Matches the padding and type of a CommandItem, the app's filter-row idiom. */
const CascadingFilterRow = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    hasChildren?: boolean;
    /** On the applied path. */
    selected: boolean;
    /** On the path the columns are currently showing. */
    browsing?: boolean;
    onSelect: () => void;
    onBrowse?: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  }
>(
  (
    { label, hasChildren, selected, browsing, onSelect, onBrowse, onKeyDown },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      onMouseEnter={onBrowse}
      onFocus={onBrowse}
      onKeyDown={onKeyDown}
      className={cn(
        'relative flex w-full select-none items-center rounded-sm px-2 py-1.5 text-left text-sm',
        'outline-none hover:bg-accent focus-visible:bg-accent',
        browsing && 'bg-accent',
        selected && 'font-medium'
      )}
    >
      <Icons.check
        className={cn('mr-2 size-4 shrink-0', !selected && 'invisible')}
      />
      <span className="truncate">{label}</span>
      {hasChildren && (
        <Icons.chevronRight className="ml-auto size-4 shrink-0 opacity-50" />
      )}
    </button>
  )
);
CascadingFilterRow.displayName = 'CascadingFilterRow';
